import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import AdminShell from "../components/AdminShell.jsx";
import AdminGalleryManager from "../components/AdminGalleryManager.jsx";
import {
  getProperty,
  updateUnitAvailability,
  updateUnitAvailabilityDate,
} from "../admin/properties.js";
import { formatDateInputValue } from "../admin/config.js";
import "../admin.css";

function UnitAdmin() {
  const { propertySlug, unitId } = useParams();
  const propertyId = decodeURIComponent(propertySlug || "");
  const decodedUnitId = decodeURIComponent(unitId || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [scheduledDate, setScheduledDate] = useState("");
  const [status, setStatus] = useState("");
  const [savingScheduledDate, setSavingScheduledDate] = useState(false);
  const [togglingAvailability, setTogglingAvailability] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadPropertyDetails() {
      try {
        const nextProperty = await getProperty(propertyId);

        if (isMounted) {
          setProperty(nextProperty);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadPropertyDetails();

    return () => {
      isMounted = false;
    };
  }, [propertyId]);

  const unit = property?.units.find(
    (currentUnit) => currentUnit.id.toLowerCase() === decodedUnitId.toLowerCase()
  );

  useEffect(() => {
    setScheduledDate(unit?.availableOn || "");
  }, [unit?.availableOn]);

  async function handleToggleAvailability() {
    if (!property || !unit) {
      return;
    }

    try {
      setTogglingAvailability(true);
      setError("");
      setStatus("");

      const nextProperty = await updateUnitAvailability(property.id, unit.id, !unit.available);

      setProperty(nextProperty);
      setStatus(
        `${unit.label} is now marked as ${unit.available ? "unavailable" : "available"}.`
      );
    } catch (toggleError) {
      setError(toggleError.message || "We could not update that unit's availability yet.");
    } finally {
      setTogglingAvailability(false);
    }
  }

  async function handleSaveScheduledDate(event) {
    event.preventDefault();

    if (!property || !unit || !scheduledDate) {
      setError("Choose a date before saving the availability schedule.");
      return;
    }

    try {
      setSavingScheduledDate(true);
      setError("");
      setStatus("");

      const nextProperty = await updateUnitAvailabilityDate(property.id, unit.id, scheduledDate);

      setProperty(nextProperty);
      setStatus(`${unit.label} is scheduled to be available on ${scheduledDate}.`);
    } catch (saveError) {
      setError(saveError.message || "We could not save that availability date yet.");
    } finally {
      setSavingScheduledDate(false);
    }
  }

  async function handleClearScheduledDate() {
    if (!property || !unit) {
      return;
    }

    try {
      setSavingScheduledDate(true);
      setError("");
      setStatus("");

      const nextProperty = await updateUnitAvailabilityDate(property.id, unit.id, "");

      setProperty(nextProperty);
      setScheduledDate("");
      setStatus("Scheduled availability date cleared.");
    } catch (clearError) {
      setError(clearError.message || "We could not clear that availability date yet.");
    } finally {
      setSavingScheduledDate(false);
    }
  }

  if (!loading && (!property || !unit)) {
    return <Navigate replace to="/admin" />;
  }

  return (
    <AdminShell
      backLabel={property?.label || "Property"}
      backTo={property ? `/admin/${encodeURIComponent(property.id)}` : "/admin"}
      subtitle={
        unit
          ? `Upload or delete the photos shown in the ${unit.label} slideshow.`
          : "Loading unit details..."
      }
      title={unit ? `${unit.label} Admin` : "Unit Admin"}
    >
      {loading || !property || !unit ? (
        <section className="admin-state-card">
          <p className="admin-state-card__eyebrow">Loading</p>
          <h1>Preparing unit tools</h1>
          <p>Please wait while we load the unit gallery settings.</p>
        </section>
      ) : (
        <>
          <section className="admin-card">
            <div className="admin-card__header">
              <div>
                <p className="admin-page__eyebrow">Availability</p>
                <h2>{unit.label} Status</h2>
                <p>Control whether this unit appears as available on the public site.</p>
              </div>
              <div className="admin-card__actions admin-card__actions--compact">
                <span
                  className={`admin-badge ${
                    unit.available
                      ? "admin-badge--success"
                      : unit.availabilityStatusClass === "scheduled"
                        ? "admin-badge--scheduled"
                        : "admin-badge--muted"
                  }`}
                >
                  {unit.availabilityStatusText}
                </span>
                <button
                  className="btn btn-outline-dark"
                  disabled={togglingAvailability}
                  onClick={handleToggleAvailability}
                  type="button"
                >
                  {togglingAvailability
                    ? "Saving..."
                    : unit.available
                      ? "Mark Unavailable"
                      : "Mark Available"}
                </button>
              </div>
            </div>

            <form className="admin-grid-form admin-grid-form--tight" onSubmit={handleSaveScheduledDate}>
              <label className="admin-input-group">
                <span>Available Again On</span>
                <input
                  min={formatDateInputValue()}
                  onChange={(event) => {
                    setScheduledDate(event.target.value);
                    setError("");
                    setStatus("");
                  }}
                  type="date"
                  value={scheduledDate}
                />
              </label>

              <div className="admin-form-actions admin-form-actions--stacked">
                <button
                  className="btn btn-primary btn-lg"
                  disabled={savingScheduledDate}
                  type="submit"
                >
                  {savingScheduledDate ? "Saving..." : "Save Available Date"}
                </button>
                <button
                  className="btn btn-outline-dark"
                  disabled={savingScheduledDate || !unit.availableOn}
                  onClick={handleClearScheduledDate}
                  type="button"
                >
                  Clear Date
                </button>
              </div>
            </form>

            {status ? <p className="admin-feedback admin-feedback--success">{status}</p> : null}
            {error ? <p className="admin-feedback admin-feedback--error">{error}</p> : null}
          </section>

          <AdminGalleryManager
            description="These images feed the unit slideshow on the public site."
            documentPath={unit.documentPath}
            emptyMessage="No unit photos have been uploaded yet."
            heading={`${unit.label} Gallery`}
            storageDirectory={unit.storagePath}
          />
        </>
      )}
    </AdminShell>
  );
}

export default UnitAdmin;
